import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

/**
 * SlideMetrics Component
 * Displays key financial metrics as animated cards
 * 
 * Features:
 * - Grid layout (2x3 or 2x2)
 * - Count-up animations
 * - Trend indicators (📈📉➡️)
 * - Color-coding based on trend
 */

const MetricCard = ({ label, value, trend, delay = 0 }) => {
  const [displayValue, setDisplayValue] = useState('0');
  const [isAnimating, setIsAnimating] = useState(true);

  // Determine colors based on trend
  const getTrendColor = () => {
    switch (trend) {
      case 'positive':
        return 'text-green-400 border-green-500/30 bg-green-500/5';
      case 'negative':
        return 'text-red-400 border-red-500/30 bg-red-500/5';
      default:
        return 'text-purple-400 border-purple-500/30 bg-purple-500/5';
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'positive':
        return '📈';
      case 'negative':
        return '📉';
      default:
        return '➡️';
    }
  };

  // Count-up animation for numbers
  useEffect(() => {
    if (!value) return;

    // Extract number from value string
    const numMatch = value.match(/[\d.,]+/);
    if (!numMatch) {
      setDisplayValue(value);
      setIsAnimating(false);
      return;
    }

    const targetNum = parseFloat(numMatch[0].replace(/\./g, '').replace(',', '.'));
    const suffix = value.replace(numMatch[0], '').trim();
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const increment = targetNum / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current += increment;
      
      if (step >= steps) {
        clearInterval(timer);
        setDisplayValue(value);
        setIsAnimating(false);
      } else {
        const formatted = current.toLocaleString('de-DE', { 
          maximumFractionDigits: value.includes(',') ? 1 : 0 
        });
        setDisplayValue(`${formatted} ${suffix}`);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      className={`relative p-6 rounded-2xl border-2 backdrop-blur-sm ${getTrendColor()} transition-all duration-300 hover:scale-105 hover:shadow-xl`}
    >
      {/* Label */}
      <div className="text-sm text-gray-400 mb-2 font-medium uppercase tracking-wide">
        {label}
      </div>

      {/* Value */}
      <div className={`text-3xl font-bold mb-2 ${isAnimating ? 'animate-pulse' : ''}`}>
        {displayValue}
      </div>

      {/* Trend Indicator */}
      {trend && (
        <div className="flex items-center space-x-1 text-sm">
          <span className="text-lg">{getTrendIcon()}</span>
          <span className="text-gray-500 capitalize">
            {trend === 'positive' ? 'Positiv' : trend === 'negative' ? 'Negativ' : 'Stabil'}
          </span>
        </div>
      )}

      {/* Decorative corner */}
      <div className="absolute top-0 right-0 w-16 h-16 opacity-10">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <circle cx="100" cy="0" r="80" fill="currentColor" />
        </svg>
      </div>
    </motion.div>
  );
};

const SlideMetrics = ({ title, metrics = [] }) => {
  // Determine grid layout based on number of metrics
  const gridCols = metrics.length <= 4 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-1 h-8 bg-purple-600 rounded-full"></div>
          <h2 className="text-4xl font-bold text-gray-800 dark:text-white">
            {title || 'Finanzkennzahlen'}
          </h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 ml-7">
          Übersicht der wichtigsten Kennzahlen
        </p>
      </motion.div>

      {/* Metrics Grid */}
      <div className={`grid ${gridCols} gap-6 flex-1`}>
        {metrics.map((metric, index) => (
          <MetricCard
            key={index}
            label={metric.label}
            value={metric.value}
            trend={metric.trend}
            delay={index * 0.1}
          />
        ))}
      </div>

      {/* Footer Note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="mt-8 text-sm text-gray-500 dark:text-gray-600 text-center"
      >
        Alle Werte basieren auf aktuellen Daten aus Clara360 • Stand: {new Date().toLocaleDateString('de-DE')}
      </motion.div>
    </div>
  );
};

export default SlideMetrics;

